import React, { useState, useEffect } from 'react';
import { clarificationService } from '../../../services/api';
import styles from './ExpertQuestions.module.css';

const ExpertQuestions = ({ consolidatedData, evaluationData, clarificationData, userPlan = 'free', hasActiveSubscription = false, onUpgradeClick, userId, projectName }) => {
  // All React hooks must be called at the top level, before any conditional logic
  const [openQuestions, setOpenQuestions] = useState({});
  const [expandedContractors, setExpandedContractors] = useState({});
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [savedAnswers, setSavedAnswers] = useState({});
  const [isRecording, setIsRecording] = useState({});
  const [answers, setAnswers] = useState({});
  const [activeQuestionKey, setActiveQuestionKey] = useState(null);
  const [recognition, setRecognition] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Simplified state for enhanced features
  const [showAddQuestion, setShowAddQuestion] = useState({});
  const [newQuestions, setNewQuestions] = useState({});
  const [uploadModal, setUploadModal] = useState({ show: false, contractorKey: null });
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
  
  // New state for questions modal
  const [questionsModal, setQuestionsModal] = useState({ show: false, contractorKey: null });
  const [modalNewQuestion, setModalNewQuestion] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState({ 
    show: false, 
    contractorKey: null, 
    questionIndex: null, 
    questionText: '' 
  });
  
  // Local state for clarification data to allow updates
  const [localClarificationData, setLocalClarificationData] = useState(clarificationData);
  const [hasLocalModifications, setHasLocalModifications] = useState(false);

  // Sync local state when prop changes (but not if we have local modifications)
  useEffect(() => {
    if (clarificationData && !hasLocalModifications) {
      setLocalClarificationData(clarificationData);
    }
  }, [clarificationData, hasLocalModifications]);

  // Plan feature mapping
  const planFeatures = {
    free: {
      canSaveAnswers: false,
      hasVoiceInput: false,
      hasAdvancedFeatures: false,
      canDownloadPDFs: false,
      canUploadPDFs: false,
      canAddQuestions: false
    },
    essential: {
      canSaveAnswers: true,
      hasVoiceInput: true,
      hasAdvancedFeatures: true,
      canDownloadPDFs: true,
      canUploadPDFs: true,
      canAddQuestions: false
    },
    complete: {
      canSaveAnswers: true,
      hasVoiceInput: true,
      hasAdvancedFeatures: true,
      canDownloadPDFs: true,
      canUploadPDFs: true,
      canAddQuestions: true
    },
    expert: {
      canSaveAnswers: true,
      hasVoiceInput: true,
      hasAdvancedFeatures: true,
      canDownloadPDFs: true,
      canUploadPDFs: true,
      canAddQuestions: true
    }
  };

  const getCurrentPlanFeatures = () => planFeatures[userPlan] || planFeatures.free;
  const isPremiumUser = userPlan !== 'free';

  // Initialize speech recognition
  useEffect(() => {
    if (!getCurrentPlanFeatures().hasVoiceInput) return;

    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        if (activeQuestionKey) {
          setAnswers(prev => ({
            ...prev,
            [activeQuestionKey]: transcript
          }));
        }
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setToast({
          type: 'error',
          message: `Speech recognition error: ${event.error}`
        });
        setIsRecording(false);
      };
      
      recognitionInstance.onend = () => {
        setIsRecording(false);
      };
      
      setRecognition(recognitionInstance);
    }
  }, [activeQuestionKey]);

  // Load uploaded files when component mounts
  useEffect(() => {
    const loadUploadedFiles = async () => {
      if (!isPremiumUser || !userId || !projectName) return;
      
      try {
        const response = await clarificationService.getUploadedFiles(userId, projectName);
        setUploadedFiles(response.data.data);
      } catch (error) {
        console.error('Error loading uploaded files:', error);
        // Fail silently - this is not critical for functionality
      }
    };

    loadUploadedFiles();
  }, [isPremiumUser, userId, projectName]);

  // Plan-based feature access control
  const canAccessCustomQuestions = () => {
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };

  const canAccessAdvancedQuestions = () => {
    return hasActiveSubscription && userPlan === 'expert';
  };

  // Check if user has access to expert questions feature
  if (!canAccessCustomQuestions()) {
    return (
      <div className={styles.lockedContent}>
        <div className={styles.lockIcon}>
          <i className="fas fa-question-circle"></i>
        </div>
        <h3>Custom Contractor Questions</h3>
        <p>Ask specific questions tailored to your project needs and get detailed insights about each contractor's approach, experience, and capabilities.</p>
        <div className={styles.featureList}>
          <ul>
            <li><i className="fas fa-check"></i> Project-specific question templates</li>
            <li><i className="fas fa-check"></i> Voice recording capabilities</li>
            <li><i className="fas fa-check"></i> PDF download & upload system</li>
            <li><i className="fas fa-check"></i> Custom question creation</li>
            <li><i className="fas fa-check"></i> Expert analysis of responses</li>
            <li><i className="fas fa-check"></i> Save and track all interactions</li>
          </ul>
        </div>
        <button className={styles.upgradeButton} onClick={onUpgradeClick}>
          <i className="fas fa-crown"></i> 
          {userPlan === 'free' 
            ? 'Upgrade to Complete Plan' 
            : userPlan === 'essential' 
            ? 'Upgrade to Complete Plan'
            : 'Upgrade to Access Questions'
          }
        </button>
        <div className={styles.planNote}>
          <i className="fas fa-info-circle"></i>
          <span>Available with Complete and Expert plans</span>
        </div>
      </div>
    );
  }

  // Handle voice recording
  const toggleRecording = (questionKey) => {
    if (!getCurrentPlanFeatures().hasVoiceInput) {
      setToast({
        type: 'info',
        message: 'Voice input is available with Essential+ plans. Upgrade to use voice-to-text!'
      });
      return;
    }

    if (!recognition) {
      setToast({
        type: 'error',
        message: 'Speech recognition is not supported in your browser'
      });
      return;
    }
    
    if (isRecording && activeQuestionKey === questionKey) {
      recognition.stop();
      setIsRecording(false);
      setActiveQuestionKey(null);
    } else {
      if (isRecording) {
        recognition.stop();
      }
      setActiveQuestionKey(questionKey);
      recognition.start();
      setIsRecording(true);
      setToast({
        type: 'info',
        message: 'Listening... Speak now'
      });
    }
  };

  // Handle answer input
  const handleAnswerChange = (questionKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));
  };

  // Save answer
  const saveAnswer = (questionKey) => {
    if (!getCurrentPlanFeatures().canSaveAnswers) {
      setToast({
        type: 'info',
        message: 'Answer saving is available with Essential+ plans. Upgrade to save your answers!'
      });
      return;
    }

    setSavedAnswers(prev => ({
      ...prev,
      [questionKey]: answers[questionKey] || ''
    }));
    
    setToast({
      type: 'success',
      message: 'Answer saved successfully!'
    });
  };

  // Download PDF functionality
  const downloadQuestionPDF = async (contractorKey, contractorIndex) => {
    if (!getCurrentPlanFeatures().canDownloadPDFs) {
      setToast({
        type: 'info',
        message: 'PDF download is available with Essential+ plans. Upgrade to download question PDFs!'
      });
      return;
    }

    setDownloadingFiles(prev => ({ ...prev, [contractorKey]: true }));
    
    try {
      // Get contractor name for better filename
      const contractorInfo = getContractorInfo(contractorKey);
      const contractorName = contractorInfo.name !== contractorKey.replace('_', ' ').toUpperCase() 
        ? contractorInfo.name 
        : '';
      
      // Use the clarification service to download the PDF
      await clarificationService.downloadFile(userId, projectName, contractorKey, contractorName);
      
      setToast({
        type: 'success',
        message: 'PDF downloaded successfully!'
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      
      let errorMessage = 'Failed to download PDF. Please try again.';
      if (error.response?.status === 404) {
        errorMessage = 'PDF file not found. Please ensure the clarification questions have been generated.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setToast({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setDownloadingFiles(prev => ({ ...prev, [contractorKey]: false }));
    }
  };

  // Function to handle file selection (before upload)
  const handleFileSelection = (contractorKey, file) => {
    if (!file || file.type !== 'application/pdf') {
      setToast({
        type: 'error',
        message: 'Please select a valid PDF file.'
      });
      return;
    }

    const existingFile = uploadedFiles[contractorKey];
    setSelectedFile(file);
    
    if (existingFile) {
      setShowOverwriteWarning(true);
    } else {
      // No existing file, proceed with upload
      setShowOverwriteWarning(false);
      handlePDFUpload(contractorKey, file);
      setSelectedFile(null);
    }
  };

  // Function to confirm overwrite and proceed with upload
  const confirmOverwriteUpload = (contractorKey) => {
    if (selectedFile) {
      setShowOverwriteWarning(false);
      handlePDFUpload(contractorKey, selectedFile);
      setSelectedFile(null);
    }
  };

  // Function to cancel file selection/overwrite
  const cancelFileSelection = () => {
    setSelectedFile(null);
    setShowOverwriteWarning(false);
    // Reset file input
    const fileInput = document.getElementById('pdfUpload');
    if (fileInput) fileInput.value = '';
  };

  // Function to preview existing uploaded file
  const previewUploadedFile = async (contractorKey) => {
    try {
      setDownloadingFiles(prev => ({ ...prev, [`${contractorKey}_preview`]: true }));
      
      // Download the response PDF and open in new tab for preview
      const response = await clarificationService.downloadResponsePDF(userId, projectName, contractorKey);
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab for preview
      const newTab = window.open(url, '_blank');
      if (!newTab) {
        // If popup blocked, fallback to download
        const link = document.createElement('a');
        link.href = url;
        link.download = `${contractorKey}_response.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setToast({
          type: 'info',
          message: 'PDF downloaded! (Popup was blocked, but file has been downloaded)'
        });
      } else {
        setToast({
          type: 'success',
          message: 'PDF opened in new tab for preview!'
        });
      }
      
      // Clean up object URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
    } catch (error) {
      console.error('Preview error:', error);
      
      let errorMessage = 'Failed to preview file. Please try again.';
      if (error.response?.status === 404) {
        errorMessage = 'Response file not found. Please upload a response first.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setToast({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setDownloadingFiles(prev => ({ ...prev, [`${contractorKey}_preview`]: false }));
    }
  };

  // Upload PDF functionality
  const handlePDFUpload = async (contractorKey, file) => {
    if (!getCurrentPlanFeatures().canUploadPDFs) {
      setToast({
        type: 'info',
        message: 'PDF upload is available with Essential+ plans. Upgrade to upload filled PDFs!'
      });
      return;
    }

    // Validate file
    if (!file) {
      setToast({
        type: 'error',
        message: 'Please select a PDF file.'
      });
      return;
    }

    if (file.type !== 'application/pdf') {
      setToast({
        type: 'error',
        message: 'Please select a PDF file.'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setToast({
        type: 'error',
        message: 'File size must be less than 10MB.'
      });
      return;
    }

    setUploadingFiles(prev => ({ ...prev, [contractorKey]: true }));
    
    try {
      // Use the clarification service to upload the PDF
      const response = await clarificationService.uploadResponsePDF(userId, projectName, contractorKey, file);
      
      // Update uploaded files state
      setUploadedFiles(prev => ({
        ...prev,
        [contractorKey]: {
          fileName: response.data.fileName,
          uploadDate: response.data.uploadDate,
          originalName: file.name
        }
      }));
      
      setUploadModal({ show: false, contractorKey: null });
      setSelectedFile(null);
      setShowOverwriteWarning(false);
      setToast({
        type: 'success',
        message: 'PDF uploaded successfully!'
      });
    } catch (error) {
      console.error('Error uploading PDF:', error);
      
      let errorMessage = 'Failed to upload PDF. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setToast({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setUploadingFiles(prev => ({ ...prev, [contractorKey]: false }));
    }
  };

  // Add custom question to specific contractor
  const addCustomQuestion = async (contractorKey) => {
    if (!getCurrentPlanFeatures().canAddQuestions) {
      setToast({
        type: 'info',
        message: 'Custom questions are available with Complete+ plans. Upgrade to add custom questions!'
      });
      return;
    }

    const questionText = newQuestions[contractorKey];
    if (!questionText || !questionText.trim()) {
      setToast({
        type: 'error',
        message: 'Please enter a question before adding.'
      });
      return;
    }

    try {
      // Save to clarification.json via API
      await clarificationService.addCustomQuestion(
        userId,
        projectName,
        contractorKey,
        questionText.trim()
      );

      // Mark that we have local modifications
      setHasLocalModifications(true);

      // Update the local clarification data state
      setLocalClarificationData(prev => {
        if (!prev || !prev[contractorKey]) return prev;
        
        const updated = { ...prev };
        updated[contractorKey] = {
          ...updated[contractorKey],
          clarificationQuestions: [
            ...(updated[contractorKey].clarificationQuestions || []),
            questionText.trim()
          ]
        };
        return updated;
      });

      // Clear the form
      setNewQuestions(prev => ({ ...prev, [contractorKey]: '' }));
      setShowAddQuestion(prev => ({ ...prev, [contractorKey]: false }));
      
      setToast({
        type: 'success',
        message: 'Custom question added successfully! PDF files are being regenerated with updated questions.'
      });
    } catch (error) {
      console.error('Error adding custom question:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.message || 'Failed to add custom question. Please try again.'
      });
    }
  };

  // Add custom question from modal
  const addCustomQuestionFromModal = async (contractorKey) => {
    if (!getCurrentPlanFeatures().canAddQuestions) {
      setToast({
        type: 'info',
        message: 'Custom questions are available with Complete+ plans. Upgrade to add custom questions!'
      });
      return;
    }

    if (!modalNewQuestion || !modalNewQuestion.trim()) {
      setToast({
        type: 'error',
        message: 'Please enter a question before adding.'
      });
      return;
    }

    try {
      // Save to clarification.json via API
      await clarificationService.addCustomQuestion(
        userId,
        projectName,
        contractorKey,
        modalNewQuestion.trim()
      );

      // Clear the modal input
      setModalNewQuestion('');
      
      // Mark that we have local modifications
      setHasLocalModifications(true);
      
      // Update the local clarification data state immediately
      setLocalClarificationData(prev => {
        if (!prev || !prev[contractorKey]) return prev;
        
        const updated = { ...prev };
        updated[contractorKey] = {
          ...updated[contractorKey],
          clarificationQuestions: [
            ...(updated[contractorKey].clarificationQuestions || []),
            modalNewQuestion.trim()
          ]
        };
        return updated;
      });
      
      setToast({
        type: 'success',
        message: 'Custom question added successfully! PDF files are being regenerated with updated questions.'
      });
    } catch (error) {
      console.error('Error adding custom question:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.message || 'Failed to add custom question. Please try again.'
      });
    }
  };

  // Show delete confirmation dialog
  const showDeleteConfirmation = (contractorKey, questionIndex, questionText) => {
    setDeleteConfirmation({
      show: true,
      contractorKey,
      questionIndex,
      questionText
    });
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setDeleteConfirmation({ 
      show: false, 
      contractorKey: null, 
      questionIndex: null, 
      questionText: '' 
    });
  };

  // Delete custom question from specific contractor
  const deleteCustomQuestion = async () => {
    if (!getCurrentPlanFeatures().canAddQuestions) {
      setToast({
        type: 'info',
        message: 'Question management is available with Complete+ plans. Upgrade to manage questions!'
      });
      cancelDelete();
      return;
    }

    const { contractorKey, questionIndex } = deleteConfirmation;

    try {
      // Delete from clarification.json via API
      await clarificationService.deleteCustomQuestion(
        userId,
        projectName,
        contractorKey,
        questionIndex
      );

      // Mark that we have local modifications
      setHasLocalModifications(true);

      // Update the local clarification data state
      setLocalClarificationData(prev => {
        if (!prev || !prev[contractorKey]) return prev;
        
        const updated = { ...prev };
        const updatedQuestions = [...(updated[contractorKey].clarificationQuestions || [])];
        updatedQuestions.splice(questionIndex, 1);
        
        updated[contractorKey] = {
          ...updated[contractorKey],
          clarificationQuestions: updatedQuestions
        };
        return updated;
      });

      // Close the confirmation dialog
      cancelDelete();
      
      setToast({
        type: 'success',
        message: 'Question deleted successfully! PDF files are being regenerated.'
      });
    } catch (error) {
      console.error('Error deleting custom question:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.message || 'Failed to delete question. Please try again.'
      });
      cancelDelete();
    }
  };

  // Toggle contractor expansion
  const toggleContractorExpansion = (contractorKey) => {
    setExpandedContractors(prev => ({
      ...prev,
      [contractorKey]: !prev[contractorKey]
    }));
  };

  // Toggle section collapse in questions modal
  const toggleSectionCollapse = (contractorKey, sectionType) => {
    const sectionKey = `${contractorKey}_${sectionType}`;
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Get contractor info from clarification data
  const getContractorInfo = (contractorKey) => {
    // First try to get from localClarificationData (which has contractor_details)
    if (localClarificationData && localClarificationData[contractorKey] && localClarificationData[contractorKey].contractor_details) {
      const details = localClarificationData[contractorKey].contractor_details;
      return {
        name: details.contractor_name || contractorKey.replace('_', ' ').toUpperCase(),
        company: details.contractor_email ? details.contractor_email.split('@')[1] : 'Company Not Specified',
        email: details.contractor_email || '',
        phone: details.contractor_phone || '',
        address: details.contractor_address || ''
      };
    }
    
    // Fallback to consolidatedData if available
    if (consolidatedData && consolidatedData[contractorKey]) {
      const contractor = consolidatedData[contractorKey];
      return {
        name: contractor.contractor?.contractor_name || contractor.contractor_info?.name || contractorKey.replace('_', ' ').toUpperCase(),
        company: contractor.contractor?.contractor_email || contractor.contractor_info?.company || contractor.contractor_info?.email || 'Company Not Specified',
        email: contractor.contractor?.contractor_email || contractor.contractor_info?.email || '',
        phone: contractor.contractor?.contractor_phone || contractor.contractor_info?.phone || '',
        address: contractor.contractor?.contractor_address || ''
      };
    }
    
    // Default fallback
    return { 
      name: contractorKey.replace('_', ' ').toUpperCase(), 
      company: 'Company Not Specified',
      email: '',
      phone: '',
      address: ''
    };
  };

  // Get color for contractor (matching other comparison components)
  const getContractorColor = (index) => {
    const colors = ['#0077cc', '#ff9800', '#4caf50', '#9c27b0', '#f44336'];
    return colors[index % colors.length];
  };

  if (!localClarificationData) {
    return (
      <div className={styles.expertQuestions}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <i className="fas fa-question-circle"></i>
          </div>
          <div className={styles.headerContent}>
            <h2>Expert Questions</h2>
            <p>Loading clarification questions...</p>
          </div>
        </div>
      </div>
    );
  }

  const contractors = Object.keys(localClarificationData);
  
  if (contractors.length === 0) {
    return (
      <div className={styles.expertQuestions}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <i className="fas fa-question-circle"></i>
          </div>
          <div className={styles.headerContent}>
            <h2>Expert Questions</h2>
            <p>No clarification questions available for this project.</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalQuestions = contractors.reduce((total, contractorKey) => {
    return total + (localClarificationData[contractorKey]?.clarificationQuestions?.length || 0);
  }, 0);

  const answeredQuestions = Object.keys(answers).filter(key => answers[key] && answers[key].trim()).length;
  const savedQuestionsCount = Object.keys(savedAnswers).filter(key => savedAnswers[key] && savedAnswers[key].trim()).length;
  const uploadedFilesCount = Object.keys(uploadedFiles).length;

  return (
    <div className={styles.expertQuestions}>
      {/* Toast notifications */}
      {toast && (
        <div className={`${styles.toast} ${styles[toast.type]}`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className={styles.toastClose}>×</button>
        </div>
      )}

      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <i className="fas fa-question-circle"></i>
        </div>
        <div className={styles.headerContent}>
          <h2>Expert Questions</h2>
          <p>Curated clarification questions to ask your contractors for better decision making</p>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statIcon}>
            <i className="fas fa-list-ul"></i>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{totalQuestions}</div>
            <div className={styles.statLabel}>Total Questions</div>
          </div>
        </div>
        
        <div className={styles.stat}>
          <div className={styles.statIcon}>
            <i className="fas fa-users"></i>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{contractors.length}</div>
            <div className={styles.statLabel}>Contractors</div>
          </div>
        </div>
        
        <div className={styles.stat}>
          <div className={styles.statIcon}>
            <i className="fas fa-edit"></i>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{answeredQuestions}</div>
            <div className={styles.statLabel}>Answered</div>
          </div>
        </div>
        
        {isPremiumUser && (
          <>
            {/* <div className={styles.stat}>
              <div className={styles.statIcon}>
                <i className="fas fa-save"></i>
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{savedQuestionsCount}</div>
                <div className={styles.statLabel}>Saved</div>
              </div>
            </div> */}
            
            <div className={styles.stat}>
              <div className={styles.statIcon}>
                <i className="fas fa-upload"></i>
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{uploadedFilesCount}</div>
                <div className={styles.statLabel}>PDFs Uploaded</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Questions by Contractor */}
      <div className={styles.contractorsSection}>
        {contractors.map((contractorKey, contractorIndex) => {
          const contractorInfo = getContractorInfo(contractorKey);
          const contractorColor = getContractorColor(contractorIndex);
          const questions = localClarificationData[contractorKey]?.clarificationQuestions || [];
          const totalContractorQuestions = questions.length;
          const isExpanded = expandedContractors[contractorKey];
          const isDownloading = downloadingFiles[contractorKey];
          const uploadedFile = uploadedFiles[contractorKey];
          
          if (questions.length === 0) return null;

          return (
            <div key={contractorKey} className={styles.contractorCard}>
              <div 
                className={styles.contractorHeader}
                onClick={() => toggleContractorExpansion(contractorKey)}
              >
                <div className={styles.contractorInfo}>
                  <div 
                    className={styles.contractorAvatar}
                    style={{ backgroundColor: contractorColor }}
                  >
                    {contractorInfo.name.charAt(0)}
                  </div>
                  <div className={styles.contractorDetails}>
                    <h3>{contractorInfo.name}</h3>
                    <p>{contractorInfo.company}</p>
                  </div>
                </div>
                
                <div className={styles.contractorMeta}>
                  <div className={styles.contractorActions}>
                    {/* Download PDF Button */}
                    <button
                      className={`${styles.downloadButton} ${!getCurrentPlanFeatures().canDownloadPDFs ? styles.premiumDisabled : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadQuestionPDF(contractorKey, contractorIndex);
                      }}
                      disabled={isDownloading || !getCurrentPlanFeatures().canDownloadPDFs}
                      title={!getCurrentPlanFeatures().canDownloadPDFs ? "Upgrade to Essential+ for PDF download" : "Download questions PDF"}
                    >
                      {!getCurrentPlanFeatures().canDownloadPDFs && <i className="fas fa-crown"></i>}
                      {isDownloading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-download"></i>
                      )}
                    </button>

                    {/* Upload PDF Button */}
                    <button
                      className={`${styles.uploadButton} ${!getCurrentPlanFeatures().canUploadPDFs ? styles.premiumDisabled : ''} ${uploadedFile ? styles.uploaded : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!getCurrentPlanFeatures().canUploadPDFs) {
                          setToast({
                            type: 'info',
                            message: 'PDF upload is available with Essential+ plans. Upgrade to upload filled PDFs!'
                          });
                        } else {
                          setUploadModal({ show: true, contractorKey });
                        }
                      }}
                      title={!getCurrentPlanFeatures().canUploadPDFs ? "Upgrade to Essential+ for PDF upload" : (uploadedFile ? "PDF uploaded" : "Upload responses PDF")}
                    >
                      {!getCurrentPlanFeatures().canUploadPDFs && <i className="fas fa-crown"></i>}
                      <i className="fas fa-upload"></i>
                    </button>

                    {/* Questions Management Button */}
                    <button
                      className={styles.questionsButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuestionsModal({ show: true, contractorKey });
                      }}
                      title="Manage questions and add custom questions"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  </div>

                  <div className={styles.questionCount}>
                    <i className="fas fa-question-circle"></i>
                    <span>{totalContractorQuestions} question{totalContractorQuestions !== 1 ? 's' : ''}</span>
                  </div>
                  <div className={styles.expandIcon}>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className={styles.questionsContainer}>
                  {/* Generated Questions */}
                  {questions.map((question, questionIndex) => {
                    const questionKey = `${contractorKey}_q${questionIndex}`;
                    const hasAnswer = answers[questionKey] && answers[questionKey].trim();
                    const isSaved = savedAnswers[questionKey] && savedAnswers[questionKey].trim();
                    const isCurrentlyRecording = isRecording && activeQuestionKey === questionKey;

                    return (
                      <div key={questionIndex} className={styles.questionCard}>
                        <div className={styles.questionHeader}>
                          <div className={styles.questionNumber}>
                            Q{questionIndex + 1}
                          </div>
                          <div className={styles.questionStatus}>
                            {isSaved && (
                              <span className={styles.savedBadge}>
                                <i className="fas fa-check"></i> Saved
                              </span>
                            )}
                            {hasAnswer && !isSaved && (
                              <span className={styles.draftBadge}>
                                <i className="fas fa-edit"></i> Draft
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className={styles.questionContent}>
                          <div className={styles.questionText}>
                            {question}
                          </div>
                          
                          <div className={styles.answerSection}>
                            <label htmlFor={`answer_${questionKey}`}>
                              Your Answer / Notes
                              {!getCurrentPlanFeatures().hasVoiceInput && (
                                <span className={styles.premiumBadge}>
                                  <i className="fas fa-crown"></i> Essential+
                                </span>
                              )}
                            </label>
                            
                            <div className={styles.answerInputContainer}>
                              <textarea
                                id={`answer_${questionKey}`}
                                value={answers[questionKey] || ''}
                                onChange={(e) => handleAnswerChange(questionKey, e.target.value)}
                                placeholder={getCurrentPlanFeatures().hasVoiceInput 
                                  ? "Type your answer or use the microphone to speak..." 
                                  : "Type your answer here... (Upgrade to Essential+ for voice input)"
                                }
                                rows={4}
                                className={styles.answerInput}
                              />
                              
                              <button 
                                type="button"
                                className={`${styles.voiceButton} ${isCurrentlyRecording ? styles.recording : ''} ${!getCurrentPlanFeatures().hasVoiceInput ? styles.premiumDisabled : ''}`}
                                onClick={() => toggleRecording(questionKey)}
                                disabled={!getCurrentPlanFeatures().hasVoiceInput}
                                title={!getCurrentPlanFeatures().hasVoiceInput 
                                  ? "Upgrade to Essential+ for voice input" 
                                  : (isCurrentlyRecording ? "Stop recording" : "Start voice input")
                                }
                              >
                                <i className={`fas ${isCurrentlyRecording ? 'fa-stop' : 'fa-microphone'}`}></i>
                              </button>
                            </div>
                            
                            {isCurrentlyRecording && (
                              <div className={styles.recordingIndicator}>
                                <div className={styles.pulse}></div>
                                <span>Recording... Speak now</span>
                              </div>
                            )}
                            
                            {hasAnswer && (
                              <div className={styles.answerActions}>
                                <button
                                  onClick={() => saveAnswer(questionKey)}
                                  className={`${styles.saveButton} ${!getCurrentPlanFeatures().canSaveAnswers ? styles.premiumDisabled : ''}`}
                                  disabled={!getCurrentPlanFeatures().canSaveAnswers}
                                  title={!getCurrentPlanFeatures().canSaveAnswers 
                                    ? "Upgrade to Essential+ to save answers" 
                                    : "Save this answer"
                                  }
                                >
                                  {!getCurrentPlanFeatures().canSaveAnswers && <i className="fas fa-crown"></i>}
                                  <i className="fas fa-save"></i>
                                  {isSaved ? 'Update' : 'Save'} Answer
                                </button>
                                
                                <button
                                  onClick={() => handleAnswerChange(questionKey, '')}
                                  className={styles.clearButton}
                                  title="Clear answer"
                                >
                                  <i className="fas fa-times"></i>
                                  Clear
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Custom Question Section */}
                  <div className={styles.addQuestionSection}>
                    {!showAddQuestion[contractorKey] ? (
                      <button
                        className={`${styles.addQuestionButton} ${!getCurrentPlanFeatures().canAddQuestions ? styles.premiumDisabled : ''}`}
                        onClick={() => {
                          if (!getCurrentPlanFeatures().canAddQuestions) {
                            setToast({
                              type: 'info',
                              message: 'Custom questions are available with Complete+ plans. Upgrade to add custom questions!'
                            });
                          } else {
                            setShowAddQuestion(prev => ({ ...prev, [contractorKey]: true }));
                          }
                        }}
                        disabled={!getCurrentPlanFeatures().canAddQuestions}
                      >
                        {!getCurrentPlanFeatures().canAddQuestions && <i className="fas fa-crown"></i>}
                        <i className="fas fa-plus"></i>
                        Add Custom Question
                      </button>
                    ) : (
                      <div className={styles.addQuestionForm}>
                        <div className={styles.questionInputContainer}>
                          <label htmlFor={`newQuestion_${contractorKey}`}>Custom Question</label>
                          <textarea
                            id={`newQuestion_${contractorKey}`}
                            value={newQuestions[contractorKey] || ''}
                            onChange={(e) => setNewQuestions(prev => ({ ...prev, [contractorKey]: e.target.value }))}
                            placeholder="Enter your custom question here... (e.g., What is your experience with similar projects in our area?)"
                            rows={3}
                            className={styles.questionInput}
                          />
                        </div>
                        
                        <div className={styles.questionActions}>
                          <button
                            onClick={() => addCustomQuestion(contractorKey)}
                            className={styles.addButton}
                            disabled={!newQuestions[contractorKey] || !newQuestions[contractorKey].trim()}
                          >
                            <i className="fas fa-plus"></i>
                            Add Question
                          </button>
                          <button
                            onClick={() => {
                              setShowAddQuestion(prev => ({ ...prev, [contractorKey]: false }));
                              setNewQuestions(prev => ({ ...prev, [contractorKey]: '' }));
                            }}
                            className={styles.cancelButton}
                          >
                            <i className="fas fa-times"></i>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Upload Modal */}
      {uploadModal.show && (
        <div className={styles.modalOverlay} onClick={() => {
          setUploadModal({ show: false, contractorKey: null });
          cancelFileSelection();
        }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.contractorInfo}>
                <div 
                  className={styles.contractorAvatar}
                  style={{ backgroundColor: getContractorColor(contractors.indexOf(uploadModal.contractorKey)) }}
                >
                  {(() => {
                    const contractorDetails = localClarificationData[uploadModal.contractorKey]?.contractor_details;
                    const contractorName = contractorDetails?.contractor_name || uploadModal.contractorKey?.replace('_', ' ').toUpperCase();
                    return contractorName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 3);
                  })()}
                </div>
                <div className={styles.contractorDetails}>
                  <h3>
                    {(() => {
                      const contractorDetails = localClarificationData[uploadModal.contractorKey]?.contractor_details;
                      return contractorDetails?.contractor_name || uploadModal.contractorKey?.replace('_', ' ').toUpperCase();
                    })()}
                  </h3>
                  <div className={styles.contractorMeta}>
                    {(() => {
                      const contractorDetails = localClarificationData[uploadModal.contractorKey]?.contractor_details;
                      return (
                        <>
                          {contractorDetails?.contractor_email && (
                            <span className={styles.contactInfo}>
                              <i className="fas fa-envelope"></i>
                              {contractorDetails.contractor_email}
                            </span>
                          )}
                          {contractorDetails?.contractor_phone && (
                            <span className={styles.contactInfo}>
                              <i className="fas fa-phone"></i>
                              {contractorDetails.contractor_phone}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <button 
                className={styles.modalClose}
                onClick={() => {
                  setUploadModal({ show: false, contractorKey: null });
                  cancelFileSelection();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.uploadInstructions}>
                <div className={styles.instructionIcon}>
                  <i className="fas fa-file-upload"></i>
                </div>
                <h4>Upload Completed PDF Response</h4>
                <p>
                  Upload the PDF response that {(() => {
                    const contractorDetails = localClarificationData[uploadModal.contractorKey]?.contractor_details;
                    return contractorDetails?.contractor_name?.split(' ')[0] || 'the contractor';
                  })()} has filled out and sent back to you.
                </p>
              </div>

              {/* Existing File Display */}
              {uploadedFiles[uploadModal.contractorKey] && (
                <div className={styles.existingFileSection}>
                  <div className={styles.existingFileHeader}>
                    <h4>
                      <i className="fas fa-file-check"></i>
                      Current Uploaded File
                    </h4>
                  </div>
                  <div className={styles.existingFile}>
                    <div className={styles.fileIcon}>
                      <i className="fas fa-file-pdf"></i>
                    </div>
                    <div className={styles.fileDetails}>
                      <div className={styles.fileName}>
                        {uploadedFiles[uploadModal.contractorKey].fileName || 'Response File'}
                      </div>
                      <div className={styles.fileMeta}>
                        <span className={styles.fileDate}>
                          <i className="fas fa-calendar"></i>
                          Uploaded: {new Date(uploadedFiles[uploadModal.contractorKey].uploadDate).toLocaleDateString()}
                        </span>
                        {uploadedFiles[uploadModal.contractorKey].size && (
                          <span className={styles.fileSize}>
                            <i className="fas fa-weight-hanging"></i>
                            {Math.round(uploadedFiles[uploadModal.contractorKey].size / 1024)} KB
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.fileActions}>
                      <button
                        className={styles.previewButton}
                        onClick={() => previewUploadedFile(uploadModal.contractorKey)}
                        disabled={downloadingFiles[`${uploadModal.contractorKey}_preview`]}
                      >
                        {downloadingFiles[`${uploadModal.contractorKey}_preview`] ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-eye"></i>
                        )}
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Overwrite Warning */}
              {showOverwriteWarning && (
                <div className={styles.overwriteWarning}>
                  <div className={styles.warningIcon}>
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <div className={styles.warningContent}>
                    <h4>Replace Existing File?</h4>
                    <p>
                      A response file already exists for this contractor. 
                      Uploading "{selectedFile?.name}" will replace the current file.
                    </p>
                    <div className={styles.warningActions}>
                      <button
                        className={styles.confirmButton}
                        onClick={() => confirmOverwriteUpload(uploadModal.contractorKey)}
                        disabled={uploadingFiles[uploadModal.contractorKey]}
                      >
                        <i className="fas fa-check"></i>
                        Replace File
                      </button>
                      <button
                        className={styles.cancelButton}
                        onClick={cancelFileSelection}
                        disabled={uploadingFiles[uploadModal.contractorKey]}
                      >
                        <i className="fas fa-times"></i>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.uploadArea}>
                <input
                  type="file"
                  id="pdfUpload"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleFileSelection(uploadModal.contractorKey, file);
                    }
                  }}
                />
                <div 
                  className={`${styles.uploadDropzone} ${uploadingFiles[uploadModal.contractorKey] ? styles.uploading : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add(styles.dragOver);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove(styles.dragOver);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove(styles.dragOver);
                    const file = e.dataTransfer.files[0];
                    if (file && file.type === 'application/pdf') {
                      handleFileSelection(uploadModal.contractorKey, file);
                    }
                  }}
                  onClick={() => document.getElementById('pdfUpload').click()}
                >
                  <div className={styles.uploadIcon}>
                    {uploadingFiles[uploadModal.contractorKey] ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-cloud-upload-alt"></i>
                    )}
                  </div>
                  
                  <div className={styles.uploadContent}>
                    <h4>
                      {uploadingFiles[uploadModal.contractorKey] ? 'Uploading...' : 'Drop your PDF here'}
                    </h4>
                    <p>
                      {uploadingFiles[uploadModal.contractorKey] 
                        ? 'Please wait while we process your file'
                        : 'or click to browse and select a file'
                      }
                    </p>
                  </div>
                  
                  {!uploadingFiles[uploadModal.contractorKey] && (
                    <button 
                      className={styles.selectFileButton}
                      type="button"
                    >
                      <i className="fas fa-file-pdf"></i>
                      Select PDF File
                    </button>
                  )}
                </div>
                
                <div className={styles.uploadMeta}>
                  <div className={styles.fileRequirements}>
                    <span className={styles.requirement}>
                      <i className="fas fa-file-pdf"></i>
                      PDF files only
                    </span>
                    <span className={styles.requirement}>
                      <i className="fas fa-weight-hanging"></i>
                      Max 10MB
                    </span>
                    <span className={styles.requirement}>
                      <i className="fas fa-shield-alt"></i>
                      Secure upload
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Questions Management Modal */}
      {questionsModal.show && questionsModal.contractorKey && (
        <div className={styles.modalOverlay} onClick={() => {
          setQuestionsModal({ show: false, contractorKey: null });
          setModalNewQuestion('');
          setCollapsedSections({});
        }}>
          <div className={`${styles.modal} ${styles.questionsModal}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.contractorInfo}>
                <div 
                  className={styles.contractorAvatar}
                  style={{ backgroundColor: getContractorColor(contractors.indexOf(questionsModal.contractorKey)) }}
                >
                  {(() => {
                    const contractorInfo = getContractorInfo(questionsModal.contractorKey);
                    return contractorInfo.name.charAt(0);
                  })()}
                </div>
                <div className={styles.contractorDetails}>
                  <h3>
                    {(() => {
                      const contractorInfo = getContractorInfo(questionsModal.contractorKey);
                      return contractorInfo.name;
                    })()}
                  </h3>
                  <p>Manage Questions</p>
                </div>
              </div>
              <button 
                className={styles.modalClose}
                onClick={() => {
                  setQuestionsModal({ show: false, contractorKey: null });
                  setModalNewQuestion('');
                  setCollapsedSections({});
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.questionsOverview}>
                <h4>
                  <i className="fas fa-list-ul"></i>
                  All Questions for This Contractor
                </h4>
                
                {/* Generated Questions */}
                {localClarificationData[questionsModal.contractorKey]?.clarificationQuestions?.length > 0 && (
                  <div className={styles.questionsSection}>
                    <div className={styles.sectionHeader}>
                      <h5>
                        <i className="fas fa-robot"></i>
                        Generated Questions ({localClarificationData[questionsModal.contractorKey].clarificationQuestions.length})
                      </h5>
                      <button
                        className={styles.collapseButton}
                        onClick={() => toggleSectionCollapse(questionsModal.contractorKey, 'generated')}
                        title={collapsedSections[`${questionsModal.contractorKey}_generated`] ? "Expand section" : "Collapse section"}
                      >
                        <i className={`fas fa-chevron-${collapsedSections[`${questionsModal.contractorKey}_generated`] ? 'down' : 'up'}`}></i>
                      </button>
                    </div>
                    {!collapsedSections[`${questionsModal.contractorKey}_generated`] && (
                      <div className={styles.questionsList}>
                        {localClarificationData[questionsModal.contractorKey].clarificationQuestions.map((question, index) => (
                          <div key={index} className={styles.questionItem}>
                            <div className={styles.questionNumber}>Q{index + 1}</div>
                            <div className={styles.questionText}>{question}</div>
                            {getCurrentPlanFeatures().canAddQuestions && (
                              <button
                                className={styles.deleteQuestionButton}
                                onClick={() => showDeleteConfirmation(questionsModal.contractorKey, index, question)}
                                title="Delete this question"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}



                {/* Add Custom Question Section */}
                <div className={styles.addQuestionSection}>
                  <div className={styles.sectionHeader}>
                    <h5>
                      <i className="fas fa-plus-circle"></i>
                      Add Custom Question
                      {!getCurrentPlanFeatures().canAddQuestions && (
                        <span className={styles.premiumBadge}>
                          <i className="fas fa-crown"></i> Complete+
                        </span>
                      )}
                    </h5>
                  </div>
                  
                  <div className={styles.addQuestionForm}>
                    <div className={styles.questionInputContainer}>
                      <label htmlFor="modalCustomQuestion">Custom Question</label>
                      <textarea
                        id="modalCustomQuestion"
                        value={modalNewQuestion}
                        onChange={(e) => setModalNewQuestion(e.target.value)}
                        placeholder={getCurrentPlanFeatures().canAddQuestions 
                          ? "Enter your custom question here... (e.g., What is your experience with similar projects in our area?)"
                          : "Upgrade to Complete+ plan to add custom questions"
                        }
                        rows={3}
                        className={styles.questionInput}
                        disabled={!getCurrentPlanFeatures().canAddQuestions}
                      />
                    </div>
                    
                    <div className={styles.questionActions}>
                      <button
                        onClick={() => addCustomQuestionFromModal(questionsModal.contractorKey)}
                        className={`${styles.addButton} ${!getCurrentPlanFeatures().canAddQuestions ? styles.premiumDisabled : ''}`}
                        disabled={!modalNewQuestion || !modalNewQuestion.trim() || !getCurrentPlanFeatures().canAddQuestions}
                      >
                        {!getCurrentPlanFeatures().canAddQuestions && <i className="fas fa-crown"></i>}
                        <i className="fas fa-plus"></i>
                        Add Question
                      </button>
                      
                      {!getCurrentPlanFeatures().canAddQuestions && (
                        <button
                          onClick={onUpgradeClick}
                          className={styles.upgradeButtonSmall}
                        >
                          <i className="fas fa-crown"></i>
                          Upgrade Plan
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className={styles.modalOverlay} onClick={cancelDelete}>
          <div className={`${styles.modal} ${styles.deleteConfirmationModal}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Confirm Delete
              </h3>
              <button 
                className={styles.closeButton}
                onClick={cancelDelete}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.deleteWarning}>
                <p>Are you sure you want to delete this question?</p>
                <div className={styles.questionPreview}>
                  <strong>Question:</strong> {deleteConfirmation.questionText}
                </div>
                <p className={styles.warningText}>
                  <i className="fas fa-info-circle"></i>
                  This action cannot be undone. The PDF files will be regenerated without this question.
                </p>
              </div>
              
              <div className={styles.modalActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={cancelDelete}
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
                <button 
                  className={styles.deleteButton}
                  onClick={deleteCustomQuestion}
                >
                  <i className="fas fa-trash"></i>
                  Delete Question
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt for Free Users */}
      {!isPremiumUser && (
        <div className={styles.upgradePrompt}>
          <div className={styles.upgradeIcon}>
            <i className="fas fa-crown"></i>
          </div>
          <div className={styles.upgradeContent}>
            <h3>Unlock Premium Question Features</h3>
            <p>Upgrade to Essential+ to unlock voice-to-text input, answer saving, PDF management, and custom question creation.</p>
            <ul className={styles.upgradeFeatures}>
              <li><i className="fas fa-microphone"></i> Voice-to-text input for all answers</li>
              <li><i className="fas fa-save"></i> Save and manage your answers</li>
              <li><i className="fas fa-file-pdf"></i> Download & upload PDF questions</li>
              <li><i className="fas fa-plus-circle"></i> Create custom questions per contractor</li>
              <li><i className="fas fa-chart-line"></i> Advanced analytics and insights</li>
            </ul>
            <button className={styles.upgradeButton} onClick={onUpgradeClick}>
              <i className="fas fa-arrow-up"></i>
              Upgrade Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertQuestions; 